import {
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps,
  styled,
  Tooltip,
  type TooltipProps,
} from "@mui/material";

const StyledIconButton = styled(MuiIconButton, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "variant",
})<{ isSelected: boolean; variant: IconButtonProps["variant"] }>(
  ({ theme, isSelected, variant }) => {
    if (variant === "text") {
      return {
        padding: 0,
        minWidth: 0,
        borderRadius: 0,
        border: "none",
        backgroundColor: "transparent",
        color: "inherit",
        "&:hover": { backgroundColor: "transparent" },
        "&:active": { backgroundColor: "transparent" },
      };
    }

    return {
      width: 30,
      height: 30,
      padding: 8,
      transition: "all 0.30s ease",
      borderRadius: "4px",
      border: "1px solid",
      borderColor: theme.palette.divider,
      backgroundColor: isSelected
        ? theme.palette.secondary.main
        : theme.palette.support[50],
      color: isSelected
        ? theme.palette.support[50]
        : theme.palette.secondary.main,
      "&:hover": {
        backgroundColor: isSelected
          ? theme.palette.secondary[800]
          : theme.palette.support[200],
      },
      "&:active": {
        backgroundColor: isSelected
          ? theme.palette.secondary.main
          : theme.palette.support[100],
      },
      ".MuiSvgIcon-root": {
        fontSize: 18,
      },
    };
  },
);

export interface IconButtonProps extends MuiIconButtonProps {
  /**
   * `filled` renders the bordered, filled button. `text` renders only the
   * children with no border, background, padding or sizing, while keeping
   * button behavior (click, keyboard, focus, disabled).
   * @default "filled"
   */
  variant?: "filled" | "text";
  isSelected?: boolean;
  tooltip: string;
  /**
   * The placement of the tooltip relative to the button.
   * @default "bottom-start"
   */
  tooltipPlacement?: TooltipProps["placement"];
}

export function IconButton({
  variant = "filled",
  isSelected = false,
  children,
  onClick,
  tooltip,
  tooltipPlacement = "bottom-start",
  disabled,
  sx,
  ...props
}: IconButtonProps) {
  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
  };

  return (
    <Tooltip
      title={tooltip}
      placement={tooltipPlacement}
      disableHoverListener={disabled}
      disableFocusListener={disabled}
      disableTouchListener={disabled}
    >
      <span>
        <StyledIconButton
          disableRipple
          disableFocusRipple
          onClick={handleOnClick}
          isSelected={isSelected}
          variant={variant}
          sx={sx}
          disabled={disabled}
          {...props}
        >
          {children}
        </StyledIconButton>
      </span>
    </Tooltip>
  );
}
